import { Component } from '../Component.js';
import { ROUNDS, BADGES, TEAM_NAMES } from '../../config/content.js';
import { BRAND, copyrightLine } from '../../config/brand.js';
import { fk, escHtml, licenceTier, scoredProgress, teamTotal, roundTotal, roundMax, teamLabel } from '../../utils/helpers.js';

const TABS = ['ctrl', 'teams', 'subs', 'badges', 'reveal', 'debrief', 'content'];
const TAB_LABELS = { ctrl:'Control', teams:'Teams', subs:'Submissions', badges:'Badges', reveal:'Reveal', debrief:'Debrief', content:'Content' };

/** Full-simulation maximum (sum of every round's max, incl. bomb) — the basis
 *  for org-wide reveal/debrief figures, which always represent a final result. */
const TOTAL_MAX = ROUNDS.reduce((sum, R) => sum + roundMax(R), 0);

export class FacilitatorView extends Component {
  #activeTab = 'ctrl';
  #subRound = 0;
  #expandedSub = null;

  constructor(el, { gameStore, firebase }) {
    super(el, {});
    this.gameStore = gameStore;
    this.firebase  = firebase;
  }

  template() {
    return `
      <div class="inner-tabs" role="tablist" aria-label="Facilitator sections">
        ${TABS.map(t => `
          <button class="inner-tab${t === this.#activeTab ? ' inner-tab--active' : ''}"
            role="tab" aria-selected="${t === this.#activeTab}" data-tab="${t}"
            aria-controls="fi-${t}" id="tab-${t}">
            ${TAB_LABELS[t]}
          </button>`).join('')}
      </div>
      ${TABS.map(t => `
        <div id="fi-${t}" role="tabpanel" aria-labelledby="tab-${t}"
          ${t !== this.#activeTab ? 'hidden' : ''}>
          ${this.#renderTab(t)}
        </div>`).join('')}
    `;
  }

  #renderTab(t) {
    switch (t) {
      case 'ctrl':    return this.#renderControl();
      case 'teams':   return this.#renderTeams();
      case 'subs':    return this.#renderSubmissions();
      case 'badges':  return this.#renderBadges();
      case 'reveal':  return this.#renderReveal();
      case 'debrief': return this.#renderDebrief();
      case 'content': return this.#renderContent();
      default: return '';
    }
  }

  #renderControl() {
    const G = this.gameStore.getState();
    const r = G.round < 0 ? 0 : G.round;
    const R = ROUNDS[r];
    const rLabel = G.round < 0
      ? `Round 1 — ${ROUNDS[0].name} (not started)`
      : `Round ${r + 1} — ${R.name}${G.active ? '' : ' (ended)'}`;

    return `
      <div class="card card--row" style="padding:8px 12px;margin-bottom:10px;align-items:center;gap:10px;flex-wrap:wrap">
        <span class="muted" style="font-size:10px;font-weight:700;letter-spacing:1px">SESSION JOIN CODE</span>
        <span class="session-code" title="Announce this code — every device enters it to join this workshop">${escHtml(this.firebase.getSessionCode?.() ?? '—')}</span>
        <span class="muted" style="font-size:10px;margin-left:auto">Teams, scorers and boards enter this code on their device to join.</span>
        <button class="btn btn--sm" id="f-sessSwitch" style="padding:4px 8px;font-size:11px;background:rgba(255,255,255,.08)">🔁 Switch session</button>
      </div>
      <div class="control-bar" role="toolbar" aria-label="Round controls">
        <span class="muted" style="font-size:11px;font-weight:700">Round:</span>
        <span id="f-rlbl" style="font-size:13px;font-weight:700;color:var(--surface-navy)">${rLabel}</span>
        <button class="btn btn--primary btn--sm" id="f-bStart" ${G.active ? 'disabled' : ''} aria-label="Start round">▶ Start</button>
        <button class="btn btn--amber   btn--sm" id="f-bBomb"  ${!G.active || G.bomb ? 'disabled' : ''} aria-label="Fire bomb drop">⚡ Bomb drop</button>
        <button class="btn btn--danger  btn--sm" id="f-bEnd"   ${!G.active ? 'disabled' : ''} aria-label="End round">■ End round</button>
        <button class="btn btn--navy    btn--sm" id="f-bNext"  ${G.active || G.round >= 2 || G.round < 0 ? 'disabled' : ''} aria-label="Next round">Next →</button>
        <button class="btn btn--danger  btn--sm" id="f-bReset" style="margin-left:auto" aria-label="Reset all data">🗑 Reset all</button>
      </div>

      <div class="timer-bar" role="timer" aria-label="Round countdown">
        <div>
          <div class="timer-bar__label">Timer</div>
          <div class="timer-display" id="f-timer">--:--</div>
        </div>
        <div class="timer-track"><div class="timer-fill" id="f-tfill" style="width:100%"></div></div>
        <div style="flex-shrink:0">
          <label for="f-dur" style="font-size:9px;color:rgba(255,255,255,.6);display:block;margin-bottom:2px">MIN</label>
          <input type="number" id="f-dur" value="${R.dur}" min="1" max="60"
            style="width:44px;padding:3px;border-radius:4px;border:none;font-size:12px;font-weight:700;text-align:center"
            aria-label="Round duration in minutes">
        </div>
      </div>

      <div class="card">
        <div class="card__heading">Round overview</div>
        <p style="margin-bottom:6px"><strong>${escHtml(R.eyebrow)}</strong> — Max ${R.max + 3} pts</p>
        <p style="margin-bottom:6px;color:var(--color-text)">${escHtml(R.sit)}</p>
        <p class="muted"><em>Bomb drop:</em> ${escHtml(R.bomb)}</p>
      </div>
    `;
  }

  #renderRoster(G) {
    const teams = Object.values(G.teams ?? {}).sort((a, b) => (a.ts ?? 0) - (b.ts ?? 0));
    const cards = teams.map(t => `
      <div class="team-card team-card--submitted">
        <h4>${escHtml(teamLabel(t.name, t.displayName))}</h4>
        <div style="font-size:10px" class="muted">${(t.members ?? []).map(escHtml).join(', ') || 'No members listed'}</div>
        <button class="btn btn--amber btn--sm" style="margin-top:6px;width:100%;justify-content:center"
          data-ret-reg="${escHtml(t.name)}" aria-label="Return ${escHtml(teamLabel(t.name, t.displayName))}'s registration for revision">↩ Return registration</button>
      </div>`).join('');

    return `<div class="card" style="margin-bottom:10px">
      <div class="card__heading">Registered teams (${teams.length} of ${TEAM_NAMES.length})</div>
      <p class="muted" style="font-size:11px;margin-bottom:10px">Shows the names and members teams entered during setup — confirm everyone has joined before starting the simulation. If a team needs to add or change members, return their registration to reopen Team Setup on their device.</p>
      ${teams.length
        ? `<div class="team-grid">${cards}</div>`
        : `<p class="muted" style="font-size:12px">No teams have registered yet — waiting for teams to complete setup.</p>`}
    </div>`;
  }

  #renderTeams() {
    const G      = this.gameStore.getState();
    const roster = this.#renderRoster(G);
    if (G.round < 0) {
      return roster + `<div class="card"><p class="muted" style="font-size:12px">Start a round to see submission status.</p></div>`;
    }
    const R = ROUNDS[G.round];
    const cards = TEAM_NAMES.map(t => {
      const label = teamLabel(t, G.teams[fk(t)]?.displayName);
      const sub = G.subs[`${fk(t)}_R${G.round}`];
      const ret = G.returned[`${fk(t)}_R${G.round}`];
      let cls = '', status = '';
      if (sub) {
        cls = 'team-card--submitted';
        status = `<span style="color:var(--color-success);font-weight:700">✓ Submitted</span>
          <button class="btn btn--amber btn--sm" style="margin-top:4px;width:100%;justify-content:center"
            data-return="${escHtml(t)}" aria-label="Return ${escHtml(label)}'s submission for revision">↩ Return</button>`;
      } else if (ret) {
        cls = 'team-card--returned';
        status = `<span style="color:var(--color-warning);font-weight:700">↩ Returned for revision</span>`;
      } else {
        status = `<span class="muted">${G.active ? 'In progress…' : 'Awaiting'}</span>`;
      }
      return `<div class="team-card ${cls}"><h4>${escHtml(label)}</h4><div style="font-size:10px">${status}</div></div>`;
    }).join('');

    return roster + `<div class="card">
      <div class="card__heading">Team submission status — Round ${G.round + 1} — ${escHtml(R.name)}</div>
      <p class="muted" style="font-size:11px;margin-bottom:10px">Click a submitted team card to return their submission for revision.</p>
      <div class="team-grid">${cards}</div>
    </div>`;
  }

  #renderSubmissions() {
    const G   = this.gameStore.getState();
    const ri  = this.#subRound;
    const roundOpts = ROUNDS.map((R, i) => `<option value="${i}"${i === ri ? ' selected' : ''}>Round ${i + 1} — ${escHtml(R.name)}</option>`).join('');

    const tiles = TEAM_NAMES.map(t => {
      const label    = teamLabel(t, G.teams[fk(t)]?.displayName);
      const sub      = G.subs[`${fk(t)}_R${ri}`];
      const expanded = this.#expandedSub === t;
      const status   = sub
        ? '<span style="color:var(--color-success);font-weight:700">✓ Submitted</span>'
        : '<span class="muted">No submission yet</span>';

      return `
        <div class="submission-tile${sub ? ' submission-tile--has-sub' : ''}">
          <button class="submission-tile__header" type="button" data-sub-toggle="${escHtml(t)}"
                  aria-expanded="${expanded}" aria-controls="sub-body-${fk(t)}">
            <span class="submission-tile__name">${escHtml(label)}</span>
            <span class="submission-tile__status">${status}</span>
            <span class="submission-tile__chev" aria-hidden="true">${expanded ? '▾' : '▸'}</span>
          </button>
          ${expanded ? `<div class="submission-tile__body" id="sub-body-${fk(t)}">${this.#renderSubDetail(t, ri)}</div>` : ''}
        </div>`;
    }).join('');

    return `
      <div class="card">
        <div class="card__heading">View completed submissions</div>
        <div style="margin-bottom:12px;max-width:320px">
          <label for="f-subRound" class="sr-only">Select round</label>
          <select id="f-subRound" aria-label="Select round">${roundOpts}</select>
        </div>
        <p class="muted" style="font-size:11px;margin-bottom:10px">Click a team to expand their submission for this round.</p>
        <div class="submission-list">${tiles}</div>
      </div>`;
  }

  #renderSubDetail(team, ri) {
    const G     = this.gameStore.getState();
    const R     = ROUNDS[ri];
    const label = teamLabel(team, G.teams[fk(team)]?.displayName);
    const sub   = G.subs[`${fk(team)}_R${ri}`];
    if (!sub) return `<p class="muted" style="font-size:12px">No submission from ${escHtml(label)} yet.</p>`;

    let h = '<div style="font-size:12px">';
    if (sub.members?.length) h += `<div style="margin-bottom:8px;padding:8px 10px;background:#f6f3ec;border-radius:var(--radius);font-size:11px;color:var(--brand-primary-dark)"><strong>Team members:</strong> ${sub.members.map(escHtml).join(', ')}</div>`;
    R.fields.forEach(f => { if (sub[f.id]) h += `<div style="margin-bottom:8px"><div style="font-size:10px;font-weight:700;color:var(--color-muted);margin-bottom:2px;text-transform:uppercase;letter-spacing:1px">${escHtml(f.label)}</div><div style="padding:8px 10px;background:#fff;border-radius:var(--radius);line-height:1.6">${escHtml(sub[f.id])}</div></div>`; });
    if (sub.bomb) h += `<div style="margin-bottom:8px"><div style="font-size:10px;font-weight:700;color:var(--color-danger);margin-bottom:2px;text-transform:uppercase;letter-spacing:1px">⚡ ${escHtml(R.bombField.label)}</div><div style="padding:8px 10px;background:#fff5f5;border:1px solid #f4c0c0;border-radius:var(--radius);line-height:1.6">${escHtml(sub.bomb)}</div></div>`;
    R.steward.forEach((q, i) => { if (sub[`sc${i}`]) h += `<div style="margin-bottom:8px"><div style="font-size:10px;font-weight:700;color:var(--brand-primary-dark);margin-bottom:2px;text-transform:uppercase;letter-spacing:1px">Steward ${i+1}: ${escHtml(q)}</div><div style="padding:8px 10px;background:#f6f3ec;border-radius:var(--radius);line-height:1.6">${escHtml(sub[`sc${i}`])}</div></div>`; });
    if (sub.sigs?.some(s => s.img)) {
      h += `<div style="margin-bottom:8px"><div style="font-size:10px;font-weight:700;color:var(--color-muted);margin-bottom:4px;text-transform:uppercase;letter-spacing:1px">Signatures</div><div style="display:flex;gap:6px;flex-wrap:wrap">`;
      sub.sigs.forEach(s => { if (s.img) h += `<div style="text-align:center"><div style="font-size:9px;color:var(--color-muted)">${escHtml(s.name)}</div><img src="${s.img}" style="height:28px;border:1px solid var(--color-border);border-radius:3px" alt="${escHtml(s.name)}'s signature"></div>`; });
      h += '</div></div>';
    }
    h += `<button class="btn btn--amber btn--sm" data-ret-team="${escHtml(team)}" data-ret-round="${ri}" style="margin-top:8px">↩ Return to team for revision</button>`;
    h += '</div>';
    return h;
  }

  #renderBadges() {
    const G        = this.gameStore.getState();
    const teamOpts = TEAM_NAMES.map(t => `<option value="${escHtml(t)}">${escHtml(teamLabel(t, G.teams[fk(t)]?.displayName))}</option>`).join('');
    const summary  = TEAM_NAMES.map(t => {
      const aw    = G.badges[fk(t)] ?? {};
      const pills = BADGES.filter(b => aw[b.id]).map(b => `<span class="badge-pill ${b.cls}">${b.icon} ${escHtml(b.label)}</span>`).join('');
      return pills ? `<div style="margin-bottom:6px"><strong style="font-size:11px">${escHtml(teamLabel(t, G.teams[fk(t)]?.displayName))}:</strong> ${pills}</div>` : '';
    }).join('') || '<p class="muted" style="font-size:12px">No badges awarded yet.</p>';

    return `
      <div class="card">
        <div class="card__heading">Award behaviour badges</div>
        <div style="margin-bottom:10px">
          <label for="f-bTeam" class="sr-only">Select team</label>
          <select id="f-bTeam" aria-label="Select team"><option value="">— select team —</option>${teamOpts}</select>
        </div>
        <div id="f-bBtns" aria-live="polite"><p class="muted" style="font-size:12px">Select a team above.</p></div>
        <div style="margin-top:12px;font-size:11px;font-weight:700;color:var(--color-muted);margin-bottom:6px;letter-spacing:1px;text-transform:uppercase">Currently awarded</div>
        <div id="f-bSum">${summary}</div>
      </div>`;
  }

  /**
   * Organisation-wide aggregate: the combined total across registered teams,
   * averaged over the number of teams that actually registered (not the 9
   * static TEAM_NAMES slots — an unstarted simulation with 3 teams shouldn't
   * be judged as if 6 more scored zero).
   */
  #orgAggregate(G) {
    const registered = TEAM_NAMES.filter(t => G.teams?.[fk(t)]);
    const total = registered.reduce((s, t) => s + teamTotal(G.scores, fk(t)), 0);
    const avg   = registered.length ? Math.round(total / registered.length) : 0;
    return { total, avg, lic: licenceTier(avg, TOTAL_MAX, true) };
  }

  #renderReveal() {
    const G          = this.gameStore.getState();
    const registered = TEAM_NAMES.filter(t => G.teams?.[fk(t)]);
    const { avg, lic } = this.#orgAggregate(G);
    const msg    = lic.tier === 'full'
      ? 'As one leadership group — you held the licence.'
      : lic.tier === 'conditional'
        ? 'The organisation achieved a conditional licence. More work to do.'
        : 'The organisation did not meet the standard. The licence is under review.';

    const rows = registered
      .map(t => ({ name: t, pts: teamTotal(G.scores, fk(t)) }))
      .sort((a, b) => b.pts - a.pts)
      .map(({ name, pts }) => {
        const { complete } = scoredProgress(G.scores, fk(name), ROUNDS);
        const l = licenceTier(pts, TOTAL_MAX, complete);
        return `<div style="display:flex;align-items:center;gap:6px;padding:5px 0;border-bottom:1px solid var(--color-border);font-size:12px">
          <span style="font-weight:600;min-width:70px">${escHtml(name)}</span>
          <span style="font-weight:700;color:var(--surface-navy);min-width:30px">${pts}</span>
          <span class="licence-badge ${l.cls}">${l.label}</span>
        </div>`;
      }).join('');

    return `
      <div class="reveal-header" aria-live="polite">
        <div class="reveal-header__label">Combined organisation score</div>
        <div class="reveal-total">${avg}</div>
        <div><span class="licence-badge ${lic.cls}" style="font-size:13px;padding:5px 12px">${lic.label} (aggregate)</span></div>
        <p style="color:rgba(255,255,255,.7);font-size:13px;margin-top:6px">${msg}</p>
      </div>
      <div class="card"><div class="card__heading">Individual scores</div>${rows}</div>
      <button class="btn btn--primary btn--sm" id="f-revRefresh">Refresh</button>`;
  }

  #renderContent() {
    return `
      <div class="card">
        <div class="card__heading">Customise simulation content</div>
        <p style="font-size:12px;line-height:1.7;margin-bottom:6px">
          Edit every piece of on-screen text — round narratives, bomb drops, questions, field labels,
          scoring dimensions, badges, branding colours and the privacy statement — without touching code.
          Changes save as a <strong>content pack</strong> shared by all devices; packs can be exported and
          imported as JSON to keep a versioned copy per client.
        </p>
        <p class="muted" style="font-size:11px;margin-bottom:10px">
          Best edited before the session starts. Devices already connected apply a new pack when their
          page next reloads. Structure (number of fields, questions and scoring dimensions) is fixed —
          only the text changes, so existing scores and submissions are never invalidated.
        </p>
        <button class="btn btn--primary btn--sm" id="f-openEditor">✏️ Open content editor</button>
      </div>`;
  }

  #renderDebrief() {
    return `
      <div class="card" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
        <span class="muted" style="font-size:12px">Generate printable debrief for all teams.</span>
        <button class="btn btn--primary btn--sm" id="f-debPrint" style="margin-left:auto">Print / PDF</button>
      </div>
      <div id="f-debPrev" aria-live="polite"></div>`;
  }

  afterRender() {
    if (this.#activeTab === 'debrief') this.#buildDebriefPreview();
  }

  bindEvents() {
    // Tab switching
    this.$$('.inner-tab').forEach(btn => {
      const handler = () => {
        this.#activeTab = btn.dataset.tab;
        this.mount();
      };
      btn.addEventListener('click', handler);
      this.addCleanup(() => btn.removeEventListener('click', handler));
    });

    // Control buttons
    this.#on('#f-bStart', 'click', () => this.#startRound());
    this.#on('#f-bBomb',  'click', () => this.#fireBomb());
    this.#on('#f-bEnd',   'click', () => this.#ctrlWrite(
      () => this.firebase.updateGame({ active: false, endMs: Date.now() }),
      { failure: 'End round did NOT reach the teams — check connection and press again.' },
    ));
    this.#on('#f-bNext',  'click', () => {
      const G = this.gameStore.getState();
      if (G.round < 2) this.#ctrlWrite(
        () => this.firebase.updateGame({ round: G.round + 1, active: false, bomb: false, endMs: 0 }),
        { failure: 'Could not advance the round — check connection and press again.' },
      );
    });
    this.#on('#f-bReset', 'click', () => this.#resetAll());

    // Return submission buttons
    this.$$('[data-return]').forEach(btn => {
      const handler = () => this.#returnSubmission(btn.dataset.return);
      btn.addEventListener('click', handler);
      this.addCleanup(() => btn.removeEventListener('click', handler));
    });

    // Return team registration buttons
    this.$$('[data-ret-reg]').forEach(btn => {
      const handler = () => this.#returnRegistration(btn.dataset.retReg);
      btn.addEventListener('click', handler);
      this.addCleanup(() => btn.removeEventListener('click', handler));
    });

    // Submissions tab — round select + expandable team tiles
    this.#on('#f-subRound', 'change', e => {
      this.#subRound = parseInt(e.target.value, 10);
      this.#expandedSub = null;
      this.mount();
    });
    this.$$('[data-sub-toggle]').forEach(btn => {
      const handler = () => {
        const team = btn.dataset.subToggle;
        this.#expandedSub = this.#expandedSub === team ? null : team;
        this.mount();
      };
      btn.addEventListener('click', handler);
      this.addCleanup(() => btn.removeEventListener('click', handler));
    });
    this.$$('[data-ret-team]').forEach(btn => {
      const handler = () => this.#returnSubmission(btn.dataset.retTeam);
      btn.addEventListener('click', handler);
      this.addCleanup(() => btn.removeEventListener('click', handler));
    });

    // Badges tab
    this.#on('#f-bTeam', 'change', () => this.#renderBadgeBtns());
    this.#on('#f-revRefresh', 'click', () => { this.#activeTab = 'reveal'; this.mount(); });
    this.#on('#f-debPrint', 'click', () => this.#printDebrief());
    this.#on('#f-openEditor', 'click', () => this.emit('content:edit'));
    this.#on('#f-sessSwitch', 'click', () => this.emit('session:switch'));
  }

  #on(sel, event, handler) {
    const el = this.$(sel);
    if (!el) return;
    el.addEventListener(event, handler);
    this.addCleanup(() => el.removeEventListener(event, handler));
  }

  /**
   * Run a facilitator control write with explicit outcome feedback. These
   * writes drive every device in the room — a silent failure (venue Wi-Fi
   * blip, App Check throttling) means the facilitator believes something
   * happened when no team ever received it. Buttons stay enabled on failure
   * (their state derives from game state, which didn't change), so the
   * recovery is simply pressing again.
   */
  async #ctrlWrite(fn, { success, failure } = {}) {
    try {
      await fn();
      if (success) this.emit('app:info', { message: success });
    } catch (e) {
      console.error('[FacilitatorView] control write failed:', e);
      this.emit('app:error', { message: failure ?? 'Action failed — check connection and try again.' });
    }
  }

  /** The bomb gets a positive confirmation as well — it's the one control
   *  whose effect is invisible from the front of the room. */
  #fireBomb() {
    return this.#ctrlWrite(
      () => this.firebase.writeGame('bomb', true),
      {
        success: '⚡ Bomb drop sent to all teams',
        failure: '⚡ Bomb drop did NOT send — check connection and press again.',
      },
    );
  }

  async #startRound() {
    const G    = this.gameStore.getState();
    const r    = G.round === -1 ? 0 : G.round;
    const mins = parseInt(document.getElementById('f-dur')?.value ?? ROUNDS[r].dur, 10);
    const durMs = mins * 60_000;
    await this.#ctrlWrite(
      () => this.firebase.updateGame({ round: r, active: true, bomb: false, endMs: Date.now() + durMs, durMs }),
      { failure: 'Round did NOT start — check connection and press again.' },
    );
  }

  async #resetAll() {
    const ok = confirm(
      '⚠️ Reset the simulation?\n\nThis permanently clears ALL data on every connected device.\n\nClick OK to reset.'
    );
    if (!ok) return;
    const ts = Date.now();
    await this.#ctrlWrite(
      () => this.firebase.resetGame({
        _reset: ts, round: -1, active: false, bomb: false, endMs: 0, durMs: 0,
        subs: {}, scores: {}, badges: {}, teams: {}, returned: {},
      }),
      { failure: 'Reset did NOT go through — check connection and try again.' },
    );
  }

  async #returnSubmission(team) {
    const G = this.gameStore.getState();
    if (!confirm(`Return ${team}'s Round ${G.round + 1} submission?\nThis will reopen the form on their iPad so they can revise and resubmit.`)) return;
    const k = `${fk(team)}_R${G.round}`;
    await this.#ctrlWrite(
      async () => {
        await this.firebase.writeGame(`returned/${k}`, Date.now());
        await this.firebase.removeGame(`subs/${k}`);
      },
      { failure: 'Return did NOT reach the team — check connection and try again.' },
    );
  }

  async #returnRegistration(team) {
    const G     = this.gameStore.getState();
    const label = teamLabel(team, G.teams[fk(team)]?.displayName);
    if (!confirm(`Return ${label}'s registration?\nThis will reopen Team Setup on their iPad so they can add or change members. Their station number and existing submissions/scores are kept.`)) return;
    await this.#ctrlWrite(
      () => this.firebase.removeGame(`teams/${fk(team)}`),
      { failure: 'Return did NOT reach the team — check connection and try again.' },
    );
  }

  #renderBadgeBtns() {
    const G    = this.gameStore.getState();
    const team = document.getElementById('f-bTeam')?.value;
    const wrap = document.getElementById('f-bBtns');
    if (!wrap) return;
    if (!team) { wrap.innerHTML = '<p class="muted" style="font-size:12px">Select a team above.</p>'; return; }

    const aw = G.badges[fk(team)] ?? {};
    wrap.innerHTML = BADGES.map(b => {
      const has = !!aw[b.id];
      return `<button class="badge-award-btn ${b.cls}${has ? ' badge-award-btn--awarded' : ''}"
        data-badge="${b.id}" data-team="${escHtml(team)}" ${has ? 'disabled' : ''} aria-pressed="${has}">
        ${b.icon} ${escHtml(b.label)}${has ? ' ✓' : ''}
      </button>`;
    }).join('');

    wrap.querySelectorAll('.badge-award-btn:not([disabled])').forEach(btn => {
      btn.addEventListener('click', async () => {
        await this.#ctrlWrite(
          () => this.firebase.writeGame(`badges/${fk(btn.dataset.team)}/${btn.dataset.badge}`, Date.now()),
          { failure: 'Badge was NOT awarded — check connection and try again.' },
        );
        setTimeout(() => this.#renderBadgeBtns(), 200);
      });
    });
  }

  #buildDebriefPreview() {
    const G       = this.gameStore.getState();
    const prev    = document.getElementById('f-debPrev');
    if (!prev) return;
    prev.innerHTML = this.#buildDebriefHtml(G);
  }

  #buildDebriefHtml(G) {
    const { avg: orgAvg, lic: orgLic } = this.#orgAggregate(G);
    const date       = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
    const registered = TEAM_NAMES.filter(t => G.teams?.[fk(t)]);
    const sorted     = registered.map(t => ({ n: t, p: teamTotal(G.scores, fk(t)) })).sort((a, b) => b.p - a.p);

    let h = `<div id="dDoc" style="font-family:Georgia,serif;color:#2a2a2a;max-width:820px;margin:0 auto;background:#fff;padding:40px 48px">`;
    h += `<div style="border-bottom:4px solid var(--brand-primary,#a28231);padding-bottom:18px;margin-bottom:20px">
      <div style="font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--brand-primary-dark,#826827);margin-bottom:5px">${escHtml(BRAND.simName)}</div>
      <h1 style="font-size:28px;font-weight:700;color:#000000;margin-bottom:3px">Debrief Summary</h1>
      <p style="font-size:12px;color:#606060">${date}</p>
    </div>`;
    h += `<div style="background:#000000;border-radius:8px;padding:18px 22px;margin-bottom:20px;display:flex;align-items:center;gap:16px">
      <div><div style="font-size:9px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:#a28231;margin-bottom:3px">Organisation result</div><div style="font-size:40px;font-weight:700;color:#fff;line-height:1">${orgAvg}</div></div>
      <div style="flex:1;border-left:1px solid rgba(255,255,255,.15);padding-left:16px"><div style="font-size:13px;font-weight:700;color:#fff;margin-bottom:3px">${orgLic.label}</div></div>
    </div>`;

    // Licence board table
    h += `<h2 style="font-size:15px;font-weight:700;color:#000000;margin-bottom:8px">Licence Board</h2>
      <table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:20px"><thead><tr style="background:#f3f1eb">
      ${['#','Team','R1','R2','R3','Total','Licence','Badges'].map(c => `<th style="padding:7px 8px;text-align:left;font-size:9px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:#606060;border-bottom:2px solid #dddddd">${c}</th>`).join('')}
      </tr></thead><tbody>`;
    sorted.forEach(({ n, p }, i) => {
      const { complete: nComplete } = scoredProgress(G.scores, fk(n), ROUNDS);
      const l2  = licenceTier(p, TOTAL_MAX, nComplete);
      const rs  = [0,1,2].map(r => { const s = G.scores[`${fk(n)}_R${r}`]; return Array.isArray(s) ? roundTotal(s) : '—'; });
      const aw  = G.badges[fk(n)] ?? {};
      const btxt = BADGES.filter(b => aw[b.id]).map(b => `${b.icon} ${b.label}`).join(', ') || '—';
      const bg  = l2.tier === 'full' ? 'background:#d4edda;color:#1d7a4a' : l2.tier === 'conditional' ? 'background:#fff3cd;color:#856404' : 'background:#f8d7da;color:#842029';
      h += `<tr style="background:${i%2===0?'#fff':'#f9f8f3'}"><td style="padding:7px 8px;color:#606060;font-weight:700">${i+1}</td><td style="padding:7px 8px;font-weight:600">${escHtml(teamLabel(n, G.teams[fk(n)]?.displayName))}</td>${rs.map(s => `<td style="padding:7px 8px;text-align:center;font-weight:600">${s}</td>`).join('')}<td style="padding:7px 8px;font-weight:700;font-size:14px">${p}</td><td style="padding:7px 8px"><span style="padding:2px 7px;border-radius:20px;font-size:10px;font-weight:700;${bg}">${l2.label}</span></td><td style="padding:7px 8px;font-size:11px">${escHtml(btxt)}</td></tr>`;
    });
    h += '</tbody></table>';

    // Per-team detail
    h += `<h2 style="font-size:15px;font-weight:700;color:#000000;margin-bottom:10px">Submission Detail</h2>`;
    registered.forEach(team => {
      const tot  = teamTotal(G.scores, fk(team));
      const { complete: tComplete } = scoredProgress(G.scores, fk(team), ROUNDS);
      const l3   = licenceTier(tot, TOTAL_MAX, tComplete);
      const aw   = G.badges[fk(team)] ?? {};
      const btxt = BADGES.filter(b => aw[b.id]).map(b => `${b.icon} ${b.label}`).join(' · ') || 'None';
      const bg3  = l3.tier === 'full' ? 'background:#d4edda;color:#1d7a4a' : l3.tier === 'conditional' ? 'background:#fff3cd;color:#856404' : 'background:#f8d7da;color:#842029';
      h += `<div style="margin-bottom:18px;border:1px solid #dddddd;border-radius:8px;overflow:hidden;page-break-inside:avoid">
        <div style="background:#000000;padding:10px 14px;display:flex;align-items:center;justify-content:space-between">
          <div style="font-size:13px;font-weight:700;color:#fff">${escHtml(teamLabel(team, G.teams[fk(team)]?.displayName))}</div>
          <div style="display:flex;align-items:center;gap:8px"><span style="font-size:16px;font-weight:700;color:#a28231">${tot} pts</span><span style="padding:2px 7px;border-radius:20px;font-size:10px;font-weight:700;${bg3}">${l3.label}</span></div>
        </div>
        <div style="padding:8px 14px;background:#f9f8f3;font-size:11px;color:#606060">Badges: ${escHtml(btxt)}</div>`;
      ROUNDS.forEach((R, ri) => {
        const sub  = G.subs[`${fk(team)}_R${ri}`];
        const sc   = G.scores[`${fk(team)}_R${ri}`] ?? [...R.dims.map(() => 0), 0];
        const rPts = roundTotal(sc);
        h += `<div style="padding:10px 14px;border-top:1px solid #dddddd">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px">
            <div style="font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#606060">Round ${ri+1} — ${escHtml(R.name)}</div>
            <div style="font-size:12px;font-weight:700;color:#000000">${rPts} / ${R.max+3}</div>
          </div>`;
        if (sub) {
          if (sub.members?.length) h += `<p style="font-size:11px;color:#826827;margin-bottom:6px"><strong>Team members:</strong> ${sub.members.map(escHtml).join(', ')}</p>`;
          R.fields.forEach(f => { if (sub[f.id]) h += `<div style="margin-bottom:5px"><div style="font-size:9px;font-weight:700;color:#606060;margin-bottom:1px">${escHtml(f.label)}</div><div style="font-size:11px;padding:5px 7px;background:#f3f1eb;border-radius:4px;line-height:1.5">${escHtml(sub[f.id])}</div></div>`; });
          if (sub.bomb) h += `<div style="margin-bottom:5px"><div style="font-size:9px;font-weight:700;color:#c0392b;margin-bottom:1px">⚡ ${escHtml(R.bombField.label)}</div><div style="font-size:11px;padding:5px 7px;background:#fff5f5;border-radius:4px;line-height:1.5">${escHtml(sub.bomb)}</div></div>`;
          R.steward.forEach((q, qi) => { if (sub[`sc${qi}`]) h += `<div style="margin-bottom:5px"><div style="font-size:9px;font-weight:700;color:#826827;margin-bottom:1px">Steward: ${escHtml(q)}</div><div style="font-size:11px;padding:5px 7px;background:#f6f3ec;border-radius:4px;line-height:1.5">${escHtml(sub[`sc${qi}`])}</div></div>`; });
          if (sub.sigs?.some(s => s.img)) { h += `<div style="display:flex;gap:6px;flex-wrap:wrap;margin:5px 0">`; sub.sigs.forEach(s => { if (s.img) h += `<div style="text-align:center"><div style="font-size:9px;color:#606060">${escHtml(s.name)}</div><img src="${s.img}" style="height:30px;border:1px solid #dddddd;border-radius:3px" alt=""></div>`; }); h += '</div>'; }
          if (Array.isArray(sc)) { h += '<div style="margin-top:5px">'; [...R.dims, R.bombDim].forEach((dim, di) => { const val = sc[di] || 0; const dot = val===3?'#a28231':val===2?'#104c97':val===1?'#e5a200':'#c0392b'; h += `<div style="display:flex;align-items:center;gap:5px;padding:2px 0;font-size:11px;border-bottom:1px solid #e8e8e8"><span style="width:16px;height:16px;border-radius:50%;background:${dot};color:#fff;display:inline-flex;align-items:center;justify-content:center;font-weight:700;font-size:9px;flex-shrink:0">${val}</span><span style="color:#606060">${escHtml(dim)}</span></div>`; }); h += '</div>'; }
        } else h += '<p style="font-size:11px;color:#606060;font-style:italic">No submission.</p>';
        h += '</div>';
      });
      h += '</div>';
    });

    h += `<div style="margin-top:6px;padding:12px 16px;background:#f3f1eb;border-radius:8px"><div style="font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#606060;margin-bottom:6px">Scoring guide</div><div style="display:grid;grid-template-columns:repeat(4,1fr);gap:4px;font-size:11px"><div><span style="font-weight:700;color:#c0392b">0</span> — Absent</div><div><span style="font-weight:700;color:#e5a200">1</span> — Attempted</div><div><span style="font-weight:700;color:#104c97">2</span> — Demonstrated</div><div><span style="font-weight:700;color:#a28231">3</span> — Exemplary</div></div></div>`;
    h += `<div style="margin-top:16px;text-align:center;font-size:10px;color:#606060;border-top:1px solid #dddddd;padding-top:12px">${escHtml(BRAND.simName)} · ${escHtml(BRAND.contactLabel)} · ${escHtml(BRAND.contactEmail)}<br>${escHtml(copyrightLine())}</div>`;
    h += '</div>';
    return h;
  }

  #printDebrief() {
    const G = this.gameStore.getState();
    const content = this.#buildDebriefHtml(G);
    const w = window.open('', '_blank');
    if (!w) { alert('Please allow popups to print the debrief.'); return; }
    w.document.write(`<!DOCTYPE html><html lang="en"><head><title>Debrief – ${BRAND.simName}</title><style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:Georgia,serif;color:#2a2a2a;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}@media print{body{margin:0}@page{margin:15mm 12mm;size:A4}}</style></head><body>${content}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  }
}
